import React, { useEffect, useMemo, useState } from 'react';
import {
  Callout, ColumnPicker, ConfirmDialog, DataTable, DensityToggle, EmptyState, Field, FilterBar,
  FormRow, ImageDropzone, NumberField, Pagination, SearchInput, SideDrawer, SummaryStrip, Toolbar,
  exportFilename, exportRows, formatThaiDate, inputCls, primaryBtnCls,
  secondaryBtnCls, useTableDensity, useToast,
  type ActiveFilter, type Column, type ImageValue, type SortState, type SummaryStat,
} from '../components';
import { Icon } from '../components/base/Icon';
import {
  DEFAULT_CODE_TTL_MINUTES, EXPIRED_UNUSED_ALERT_PERCENT, PRIVILEGES,
  PRIVILEGE_BENEFIT_LABEL, PRIVILEGE_RECURRENCE_LABEL, PRIVILEGE_STATUS_ICON, PRIVILEGE_STATUS_LABEL,
  TIERS, formatDuration,
  type Privilege, type PrivilegeBenefitKind, type PrivilegeRecurrence, type PrivilegeStatus, type TierId,
} from './loyaltyData';

/**
 * PrivilegeScreen — สิทธิพิเศษแบบกดรับ
 *
 * สิทธิ์แบบกดรับทำงานสองจังหวะ: ลูกค้า "กดรับ" แล้วได้รหัส → เอารหัสไป "ใช้"
 * ระหว่างสองจังหวะมีนาฬิกาจับเวลาอยู่ (ค่าตั้งต้น 15 นาที) และรหัสที่หมดเวลาก่อนใช้จะหายไป
 *
 * หน้านี้ถูกออกแบบรอบตัวเลขตัวเดียว: **จำนวนครั้งที่หมดอายุโดยไม่ได้ใช้**
 *
 * เหตุผล: ตัวเลขนั้นวัดว่ากติกาเวลาที่เราตั้งไว้ตรงกับชีวิตจริงของลูกค้าหรือไม่
 * ถ้าคนกดรับ 100 ครั้งแล้วใช้ 60 หมดอายุ 40 ปัญหาไม่ใช่ "ลูกค้าไม่สนใจ"
 * แต่คือ 15 นาทีไม่พอสำหรับคนที่กดรับตอนเห็นแบนเนอร์แล้วตั้งใจไปใช้ที่ร้านตอนเย็น
 * ตัวเลขนี้จึงอยู่ในแถบสรุปด้านบน อยู่ในตารางเป็นคอลัมน์ของตัวเอง พร้อมเปอร์เซ็นต์
 * และเมื่อเกิน 25% หน้าจอเสนอให้ขยายเวลาโดยตรงในกล่องแก้ไข ไม่ต้องให้คนหลังบ้านสรุปเอง
 *
 * ⚠ ค่า 15 นาทีมาจาก mockup แอป ไม่ได้อยู่ใน SRS — ต้องยืนยันก่อนขึ้นระบบ
 *   ถ้าเวลาสั้นเกินไป ลูกค้าเสียสิทธิ์ที่ควรได้ ถ้ายาวเกินไป คนกดเก็บรหัสไว้ล่วงหน้า
 *   แล้วสิทธิ์ที่ตั้งใจให้จำกัดจำนวนจะถูกจองไว้หมดโดยยังไม่มีใครใช้
 */

type ScreenState = 'ready' | 'loading' | 'error';

const pctUnused = (p: Privilege) => (p.claimed === 0 ? 0 : Math.round((p.expiredUnused / p.claimed) * 100));
const pctUsed = (p: Privilege) => (p.claimed === 0 ? 0 : Math.round((p.used / p.claimed) * 100));

/** สรุปสิทธิ์เป็นประโยคเดียวสำหรับตารางและไฟล์ส่งออก */
const benefitText = (p: Privilege): string => {
  const kind = p.benefitKind ?? 'percent';
  const value = (p.value ?? 0).toLocaleString('th-TH');
  if (kind === 'percent') {
    const cap = p.maxDiscount ? ` (สูงสุด ${p.maxDiscount.toLocaleString('th-TH')} บาท)` : '';
    return `ลด ${value}%${cap}`;
  }
  if (kind === 'amount') return `ลด ${value} บาท`;
  if (kind === 'shipping') return (p.value ?? 0) > 0 ? `ฟรีค่าส่งไม่เกิน ${value} บาท` : 'ฟรีค่าจัดส่ง';
  if (kind === 'product') return `แถมสินค้า ${value} ชิ้น`;
  return `แถมบริการ ${value} รายการ`;
};

const CHANNEL_LABEL: Record<Privilege['channel'], string> = {
  pos: 'หน้าร้าน',
  app: 'ในแอป',
  both: 'ทั้งสองช่องทาง',
};

/** ตัวเลือกเวลาที่ตั้งได้ พร้อมผลที่ตามมา — ไม่ให้กรอกนาทีเปล่า ๆ */
/**
 * ⚠️ ค่านี้ยังไม่มีในงานออกแบบ — ใช้ชุดเดียวกับภาพของระดับสมาชิก (16:9)
 *    เพราะรูปสิทธิพิเศษขึ้นเป็นการ์ดกว้างเต็มจอในแอปเหมือนกัน
 *    ต้องยืนยันกับทีมออกแบบก่อนใช้งานจริง
 */
const IMAGE_SPEC = {
  aspect: '16:9' as const,
  minWidth: 1_280,
  maxKB: 400,
  usedAt: 'การ์ดสิทธิพิเศษในแอป กว้างเต็มจอ 390px',
};

const TTL_PRESETS: Array<{ minutes: number; label: string; note: string }> = [
  { minutes: 5, label: '5 นาที', note: 'สำหรับสิทธิ์ที่ใช้ที่เคาน์เตอร์ทันที เช่น ส่วนลดหน้าร้านที่พนักงานสแกนให้' },
  { minutes: 15, label: '15 นาที', note: 'ค่าตั้งต้น — พอสำหรับคนที่กดรับแล้วชำระเงินต่อในแอปทันที' },
  { minutes: 60, label: '1 ชั่วโมง', note: 'เผื่อเวลาให้คนที่กดรับแล้วเลือกสินค้าต่ออีกสักพัก' },
  { minutes: 1_440, label: '1 วัน', note: 'สำหรับสิทธิ์ที่ต้องเดินทางไปใช้ เช่น คลินิกหรือกิจกรรม' },
  { minutes: 10_080, label: '7 วัน', note: 'สิทธิ์ที่ต้องนัดหมายล่วงหน้า — แต่ต้องระวังคนกดเก็บไว้ก่อนโดยไม่ใช้' },
];

const StatusPill: React.FC<{ status: PrivilegeStatus }> = ({ status }) => {
  const style: Record<PrivilegeStatus, { bg: string; fg: string }> = {
    draft: { bg: 'var(--surface-100)', fg: 'var(--ink-700)' },
    scheduled: { bg: 'var(--info-bg)', fg: 'var(--info-text)' },
    active: { bg: 'var(--success-bg)', fg: 'var(--success-text)' },
    paused: { bg: 'var(--warning-bg)', fg: 'var(--warning-text)' },
    ended: { bg: 'var(--surface-100)', fg: 'var(--ink-700)' },
  };
  const s = style[status];
  return (
    <span
      className="inline-flex items-center gap-1 rounded-[var(--radius-pill)] px-2 py-[var(--space-05)] text-[length:var(--text-table-sm)] font-[var(--weight-medium)]"
      style={{ background: s.bg, color: s.fg }}
    >
      <Icon name={PRIVILEGE_STATUS_ICON[status]} size={14} filled />
      {PRIVILEGE_STATUS_LABEL[status]}
    </span>
  );
};

/** แถบสามช่วง: ใช้แล้ว · ยังไม่ถึงกำหนด · หมดอายุโดยไม่ได้ใช้ */
const ClaimFunnel: React.FC<{ p: Privilege; height?: number }> = ({ p, height = 10 }) => {
  const pending = Math.max(0, p.claimed - p.used - p.expiredUnused);
  const seg = (n: number) => (p.claimed === 0 ? 0 : (n / p.claimed) * 100);
  return (
    <span
      role="img"
      aria-label={`กดรับ ${p.claimed} ครั้ง · ใช้แล้ว ${p.used} · รอใช้ ${pending} · หมดอายุโดยไม่ได้ใช้ ${p.expiredUnused}`}
      className="flex overflow-hidden rounded-[var(--radius-pill)] bg-[var(--surface-100)]"
      style={{ height }}
    >
      <span style={{ width: `${seg(p.used)}%`, background: 'var(--success-solid)' }} />
      <span style={{ width: `${seg(pending)}%`, background: 'var(--info-solid)' }} />
      <span style={{ width: `${seg(p.expiredUnused)}%`, background: 'var(--warning-solid)' }} />
    </span>
  );
};

export interface PrivilegeScreenProps {
  initialState?: ScreenState;
  /** สิทธิพิเศษจากชั้นข้อมูล — ไม่ส่งมา = ใช้ข้อมูลจำลอง */
  privileges?: Privilege[];
  /** ระดับสมาชิกจริงของ division — ไม่ส่งมา = ใช้ระดับจำลอง */
  tiers?: Array<{ id: TierId; name: string; memberCount: number }>;
  /** บันทึกจริง — ไม่ส่งมา = แก้ในเครื่องอย่างเดียว (โหมดสาธิต) */
  onSave?: (p: Privilege) => Promise<void>;
  onRefresh?: () => void;
  refreshing?: boolean;
  /**
   * ส่วนที่ระบบหลังบ้านยังให้ไม่ได้ — ซ่อนคอลัมน์และตัวเลขของส่วนนั้น
   * ตัวเลขที่เป็น 0 ตลอดอ่านว่า "ไม่มีใครใช้" ซึ่งอันตรายกว่าไม่มีตัวเลข
   */
  unsupportedFields?: Array<{ key: 'used'; label: string }>;
}

export const PrivilegeScreen: React.FC<PrivilegeScreenProps> = ({
  initialState = 'ready',
  privileges: privilegesProp,
  tiers: tiersProp,
  onSave,
  onRefresh,
  refreshing = false,
  unsupportedFields,
}) => {
  const toast = useToast();
  const [screenState, setScreenState] = useState<ScreenState>(initialState);
  const [rows, setRows] = useState<Privilege[]>(privilegesProp ?? PRIVILEGES);

  /* ข้อมูลจากชั้น API มาทีหลัง — ต้องรับค่าใหม่เมื่อมันมาถึง */
  useEffect(() => { if (privilegesProp) setRows(privilegesProp); }, [privilegesProp]);
  useEffect(() => { setScreenState(initialState); }, [initialState]);

  const tierOptions = tiersProp ?? TIERS;
  /**
   * ระบบหลังบ้านหาสิทธิ์จากระดับสมาชิกที่ผูกไว้
   *
   * ⚠️ สิทธิ์ที่ไม่ผูกระดับใดเลย **สร้างสำเร็จแต่หายจากรายการทันที** แก้หรือลบต่อไม่ได้
   *    จึงบังคับให้เลือกอย่างน้อยหนึ่งระดับเมื่อต่อ backend จริง
   */
  const requireTier = Boolean(onSave);

  const invalidReason = (p: Privilege): string | null => {
    if (!p.name.trim()) return 'ต้องตั้งชื่อสิทธิ์ก่อน';
    if (requireTier && p.audience.tiers.length === 0) return 'ต้องเลือกระดับสมาชิกอย่างน้อยหนึ่งระดับ';

    const kind = p.benefitKind ?? 'percent';
    const value = p.value ?? 0;
    /* ฟรีค่าจัดส่งใส่ 0 ได้ แปลว่าครอบคลุมค่าส่งทั้งหมด */
    if (kind !== 'shipping' && value <= 0) return 'ต้องกรอกมูลค่าของสิทธิ์';
    if (kind === 'percent' && value > 100) return 'ส่วนลดเป็นเปอร์เซ็นต์ต้องไม่เกิน 100%';
    return null;
  };

  /** หน่วยของช่องมูลค่า เปลี่ยนตามประเภทสิทธิ์ */
  const valueUnit = (kind: PrivilegeBenefitKind): string => {
    if (kind === 'percent') return '%';
    if (kind === 'product' || kind === 'service') return 'ชิ้น';
    return 'บาท';
  };
  /** true = ซ่อนทุกอย่างที่คำนวณจาก "ใช้จริง" */
  const hideUsed = (unsupportedFields ?? []).some((f) => f.key === 'used');
  const [density, setDensity] = useTableDensity();

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | PrivilegeStatus>('all');
  const [tierFilter, setTierFilter] = useState<'all' | TierId>('all');
  const [problemOnly, setProblemOnly] = useState(false);
  const [sort, setSort] = useState<SortState | null>({ columnId: 'expiredUnused', direction: 'desc' });
  const [visibleCols, setVisibleCols] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [edit, setEdit] = useState<Privilege | null>(null);
  const [confirmSave, setConfirmSave] = useState<Privilege | null>(null);

  /* ---------------------------------------------------------------- ตัวกรอง */

  const filtered = useMemo(() => rows.filter((p) => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (tierFilter !== 'all' && !p.audience.tiers.includes(tierFilter)) return false;
    if (problemOnly && pctUnused(p) < EXPIRED_UNUSED_ALERT_PERCENT) return false;
    if (query && !p.name.includes(query.trim())) return false;
    return true;
  }), [rows, statusFilter, tierFilter, problemOnly, query]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const key = (p: Privilege): string | number => {
      switch (sort.columnId) {
        case 'claimed': return p.claimed;
        case 'used': return p.used;
        case 'expiredUnused': return pctUnused(p);
        case 'codeTtlMinutes': return p.codeTtlMinutes;
        case 'window': return p.to;
        default: return p.name;
      }
    };
    return [...filtered].sort((a, b) => {
      const ka = key(a); const kb = key(b);
      return (typeof ka === 'number' && typeof kb === 'number' ? ka - kb : String(ka).localeCompare(String(kb), 'th')) * dir;
    });
  }, [filtered, sort]);

  const pageRows = sorted.slice((page - 1) * pageSize, page * pageSize);

  /* --------------------------------------------------------------- คอลัมน์ */

  const allColumns: Array<Column<Privilege>> = useMemo(() => [
    {
      id: 'name', header: 'ชื่อสิทธิ์', width: 268, pinned: true, required: true, sortable: true,
      value: (p) => p.name,
      cell: (p) => (
        <span className="flex flex-col leading-[1.3]">
          <span className="bo-truncate font-[var(--weight-medium)] text-[var(--ink-900)]">{p.name}</span>
          <span className="bo-truncate text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
            {p.audience.tiers.length === 0 ? 'ทุกระดับ' : p.audience.tiers.map((t) => TIERS.find((x) => x.id === t)?.name).join(' · ')}
            {p.audience.note ? ` · ${p.audience.note}` : ''}
          </span>
        </span>
      ),
    },
    {
      id: 'benefit', header: 'สิทธิ์ที่ได้', width: 208,
      value: (p) => benefitText(p),
      cell: (p) => (
        <span className="flex flex-col leading-[1.3]">
          <span className="bo-truncate text-[var(--ink-900)]">{benefitText(p)}</span>
          {(p.minSpend ?? 0) > 0 && (
            <span className="bo-num text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
              ซื้อขั้นต่ำ {(p.minSpend ?? 0).toLocaleString('th-TH')} บาท
            </span>
          )}
        </span>
      ),
    },
    { id: 'status', header: 'สถานะ', width: 124, value: (p) => PRIVILEGE_STATUS_LABEL[p.status], cell: (p) => <StatusPill status={p.status} /> },
    {
      id: 'window', header: 'ช่วงเวลาที่ใช้ได้', width: 176, sortable: true,
      value: (p) => `${p.from} – ${p.to}`,
      cell: (p) => (
        <span className="flex flex-col leading-[1.3] text-[length:var(--text-table-sm)]">
          <span className="bo-num" style={{ textAlign: 'left' }}>{formatThaiDate(p.from)}</span>
          <span className="bo-num text-[var(--ink-700)]" style={{ textAlign: 'left' }}>ถึง {formatThaiDate(p.to)}</span>
        </span>
      ),
    },
    {
      id: 'codeTtlMinutes', header: 'อายุรหัสหลังกดรับ', width: 156, numeric: true, sortable: true,
      value: (p) => p.codeTtlMinutes,
      cell: (p) => (
        <span className="inline-flex items-center gap-1">
          <span className="bo-num font-[var(--weight-medium)]">{formatDuration(p.codeTtlMinutes)}</span>
          {p.codeTtlMinutes !== DEFAULT_CODE_TTL_MINUTES && (
            <span className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">ไม่ใช่ค่าตั้งต้น</span>
          )}
        </span>
      ),
    },
    { id: 'channel', header: 'ใช้ได้ที่', width: 136, value: (p) => CHANNEL_LABEL[p.channel] },
    {
      id: 'limitPerPerson', header: 'สิทธิ์ต่อคน', width: 108, numeric: true,
      value: (p) => p.limitPerPerson ?? '',
      cell: (p) => (p.limitPerPerson === null
        ? <span className="text-[var(--ink-700)]">ไม่จำกัด</span>
        : <span className="bo-num">{p.limitPerPerson.toLocaleString('th-TH')}</span>),
    },
    { id: 'claimed', header: 'กดรับไปแล้ว', width: 124, numeric: true, sortable: true, value: (p) => p.claimed, cell: (p) => <span className="bo-num">{p.claimed.toLocaleString('th-TH')}</span> },
    {
      id: 'used', header: 'ใช้จริง', width: 148, numeric: true, sortable: true,
      value: (p) => p.used,
      cell: (p) => (
        <span className="inline-flex items-center justify-end gap-[var(--space-15)]">
          <span className="bo-num">{p.used.toLocaleString('th-TH')}</span>
          <span className="bo-num text-[length:var(--text-table-sm)] text-[var(--ink-700)]">{pctUsed(p)}%</span>
        </span>
      ),
    },
    {
      id: 'expiredUnused', header: 'หมดอายุโดยไม่ได้ใช้', width: 208, numeric: true, sortable: true,
      value: (p) => pctUnused(p),
      cell: (p) => {
        const pct = pctUnused(p);
        const high = pct >= EXPIRED_UNUSED_ALERT_PERCENT;
        return (
          <span className="inline-flex items-center justify-end gap-[var(--space-15)]">
            <span className="bo-num font-[var(--weight-semibold)]" style={{ color: high ? 'var(--error-text)' : 'var(--ink-900)' }}>
              {p.expiredUnused.toLocaleString('th-TH')}
            </span>
            <span className="bo-num text-[length:var(--text-table-sm)]" style={{ color: high ? 'var(--error-text)' : 'var(--ink-700)' }}>
              {pct}%
            </span>
            {high && (
              <span className="inline-flex items-center gap-1 text-[length:var(--text-table-sm)] text-[var(--error-text)]">
                <Icon name="timer_off" size={14} filled />
                สูงผิดปกติ
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: 'funnel', header: 'สัดส่วน ใช้ / รอใช้ / หมดอายุ', width: 200,
      value: (p) => `${pctUsed(p)}% / ${pctUnused(p)}%`,
      cell: (p) => <ClaimFunnel p={p} />,
    },
    {
      id: 'actions', header: 'จัดการ', width: 92, align: 'right', value: () => '',
      cell: (p) => (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setEdit(p); }}
          className="min-h-[var(--hit-table)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-table-sm)] font-[var(--weight-medium)] text-[var(--blue-700)] underline underline-offset-2 outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
        >
          แก้ไข
        </button>
      ),
    },
  ], []);

  /* คอลัมน์ที่คำนวณจาก "ใช้จริง" ถูกซ่อนเมื่อระบบหลังบ้านยังไม่ตัดรหัส */
  const columns = useMemo(
    () => (hideUsed ? allColumns.filter((c) => c.id !== 'used' && c.id !== 'funnel') : allColumns),
    [allColumns, hideUsed],
  );

  const visible = visibleCols.length ? visibleCols : columns.map((c) => c.id);

  /* ---------------------------------------------------------------- สรุป */

  const totals = useMemo(() => {
    const claimed = rows.reduce((s, p) => s + p.claimed, 0);
    const used = rows.reduce((s, p) => s + p.used, 0);
    const expired = rows.reduce((s, p) => s + p.expiredUnused, 0);
    return {
      claimed, used, expired,
      expiredPct: claimed ? Math.round((expired / claimed) * 100) : 0,
      problemCount: rows.filter((p) => p.claimed > 0 && pctUnused(p) >= EXPIRED_UNUSED_ALERT_PERCENT).length,
      active: rows.filter((p) => p.status === 'active').length,
    };
  }, [rows]);

  const stats: SummaryStat[] = [
    { label: 'สิทธิ์ที่ใช้งานอยู่', value: totals.active.toLocaleString('th-TH') + ' รายการ', icon: 'play_circle', tone: 'brand' },
    { label: 'กดรับไปแล้ว', value: totals.claimed.toLocaleString('th-TH') + ' ครั้ง', icon: 'touch_app' },
    ...(hideUsed ? [] : [
      { label: 'ใช้จริง', value: totals.used.toLocaleString('th-TH') + ' ครั้ง', icon: 'check_circle', tone: 'success' } as SummaryStat,
    ]),
    {
      label: 'หมดอายุโดยไม่ได้ใช้', value: `${totals.expired.toLocaleString('th-TH')} ครั้ง (${totals.expiredPct}%)`,
      icon: 'timer_off', tone: totals.expiredPct >= EXPIRED_UNUSED_ALERT_PERCENT ? 'error' : 'default',
      note: totals.expiredPct >= EXPIRED_UNUSED_ALERT_PERCENT ? 'กติกาเวลาน่าจะสั้นเกินไป' : undefined,
    },
  ];

  const activeFilters: ActiveFilter[] = useMemo(() => {
    const out: ActiveFilter[] = [];
    if (statusFilter !== 'all') out.push({ id: 'status', field: 'สถานะ', display: PRIVILEGE_STATUS_LABEL[statusFilter] });
    if (tierFilter !== 'all') out.push({ id: 'tier', field: 'ระดับสมาชิก', display: TIERS.find((t) => t.id === tierFilter)?.name ?? '' });
    if (problemOnly) out.push({ id: 'problem', field: 'เฉพาะที่มีปัญหา', display: `หมดอายุไม่ได้ใช้ ≥ ${EXPIRED_UNUSED_ALERT_PERCENT}%` });
    return out;
  }, [statusFilter, tierFilter, problemOnly]);

  const clearAll = () => { setStatusFilter('all'); setTierFilter('all'); setProblemOnly(false); setQuery(''); setPage(1); };

  const doExport = (format: 'csv' | 'excel') => {
    const n = exportRows({
      columns, rows: sorted, columnIds: visible.filter((id) => id !== 'actions' && id !== 'funnel'),
      filename: exportFilename('สิทธิพิเศษแบบกดรับ'), format,
    });
    toast.show({ tone: 'success', message: `ส่งออก ${n.toLocaleString('th-TH')} รายการแล้ว — บันทึกใน Audit Log` });
  };

  const save = (p: Privilege, reason?: string) => {
    const before = rows;
    setRows((prev) => (prev.some((x) => x.id === p.id)
      ? prev.map((x) => (x.id === p.id ? { ...p, provisional: false } : x))
      : [{ ...p, provisional: false }, ...prev]));
    setEdit(null);
    setConfirmSave(null);

    /* โหมดสาธิต — แก้ในเครื่องอย่างเดียว จึงเลิกทำได้ */
    if (!onSave) {
      toast.show({
        tone: 'success',
        message: `บันทึก "${p.name}" แล้ว — บันทึกใน Audit Log`,
        undo: { onUndo: () => setRows(before), seconds: 10 },
      });
      void reason;
      return;
    }

    /* ต่อ backend จริง — ไม่มีปุ่มเลิกทำ ถ้าล้มเหลวคืนตารางกลับแล้วบอกตรง ๆ */
    void onSave(p)
      .then(() => toast.show({ tone: 'success', message: `บันทึก "${p.name}" แล้ว` }))
      .catch((err: unknown) => {
        setRows(before);
        toast.show({
          tone: 'error',
          message: err instanceof Error ? err.message : `บันทึก "${p.name}" ไม่สำเร็จ`,
        });
      });
    void reason;
  };

  return (
    <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-0)] font-[family-name:var(--font-thai)]">
      <Toolbar
        title="สิทธิพิเศษแบบกดรับ"
        count={sorted.length}
        center={<SearchInput value={query} onChange={(v) => { setQuery(v); setPage(1); }} focusOnSlash placeholder="ค้นหาชื่อสิทธิ์" width={300} />}
        end={
          <>
            <button type="button" className={primaryBtnCls} onClick={() => setEdit({
              id: `pv-${Date.now()}`, name: '', audience: { tiers: [] }, status: 'draft',
              benefitKind: 'percent', value: 10, maxDiscount: null, minSpend: 0, allProducts: true,
              from: '2026-09-01', to: '2026-09-30', codeTtlMinutes: DEFAULT_CODE_TTL_MINUTES,
              limitPerPerson: 1, recurrence: 'monthly', totalLimit: null, hasClaimButton: true,
              claimed: 0, used: 0, expiredUnused: 0, channel: 'app', provisional: false,
            })}>
              <Icon name="add" size={17} />
              สร้างสิทธิ์ใหม่
            </button>
            {onRefresh && (
              <button type="button" onClick={onRefresh} disabled={refreshing} className={secondaryBtnCls}>
                <Icon name="refresh" size={17} className={refreshing ? 'animate-spin' : undefined} />
                {refreshing ? 'กำลังโหลด' : 'โหลดใหม่'}
              </button>
            )}
            <DensityToggle value={density} onChange={setDensity} />
            <ColumnPicker columns={columns} persistKey="privileges" value={visible} onChange={setVisibleCols} />
            <div className="flex items-center gap-[var(--space-05)] rounded-[var(--radius-sm)] border border-[var(--line-300)] p-[var(--space-05)]">
              <button type="button" onClick={() => doExport('excel')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center gap-1 rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">
                <Icon name="download" size={16} />Excel
              </button>
              <button type="button" onClick={() => doExport('csv')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">CSV</button>
            </div>
          </>
        }
      />

      {/* ⚠️ ตัวเลขสรุปคำนวณจากรายการในตาราง — ระหว่างโหลดยังไม่มีของจริง
          เคยแสดงค่าจากข้อมูลจำลองจนอ่านเหมือนตัวเลขจริง */}
      {screenState !== 'loading' && <SummaryStrip stats={stats} />}


      {screenState !== 'loading' && totals.problemCount > 0 && (
        <div className="border-b border-[var(--line-200)] px-[var(--space-4)] py-[var(--space-2)]">
          <Callout
            tone="error"
            title={`${totals.problemCount} สิทธิ์มีอัตราหมดอายุโดยไม่ได้ใช้เกิน ${EXPIRED_UNUSED_ALERT_PERCENT}%`}
            action={
              <button type="button" onClick={() => { setProblemOnly(true); setPage(1); }} className={secondaryBtnCls}>
                ดูเฉพาะรายการเหล่านี้
              </button>
            }
          >
            ตัวเลขนี้บอกว่าลูกค้ากดรับแล้วใช้ไม่ทัน ไม่ใช่ว่าลูกค้าไม่สนใจ —
            ก่อนสรุปว่าแคมเปญไม่ได้ผล ให้ตรวจว่าอายุรหัสหลังกดรับสั้นเกินไปสำหรับวิธีใช้จริงหรือเปล่า
            (สิทธิ์ที่ต้องเดินทางไปใช้ที่ร้านหรือคลินิกไม่ควรตั้ง 15 นาที)
          </Callout>
        </div>
      )}

      <FilterBar
        active={activeFilters}
        onRemove={(id) => {
          if (id === 'status') setStatusFilter('all');
          if (id === 'tier') setTierFilter('all');
          if (id === 'problem') setProblemOnly(false);
          setPage(1);
        }}
        onClearAll={clearAll}
        currentFilters={{ statusFilter, tierFilter, problemOnly }}
        persistKey="privileges"
        resultCount={sorted.length}
      >
        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          สถานะ
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as typeof statusFilter); setPage(1); }} className={inputCls}>
            <option value="all">ทั้งหมด</option>
            {(Object.keys(PRIVILEGE_STATUS_LABEL) as PrivilegeStatus[]).map((s) => (
              <option key={s} value={s}>{PRIVILEGE_STATUS_LABEL[s]}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          ระดับสมาชิก
          <select value={tierFilter} onChange={(e) => { setTierFilter(e.target.value as typeof tierFilter); setPage(1); }} className={inputCls}>
            <option value="all">ทุกระดับ</option>
            {TIERS.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-[var(--space-15)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          <input
            type="checkbox"
            checked={problemOnly}
            onChange={(e) => { setProblemOnly(e.target.checked); setPage(1); }}
            className="h-4 w-4 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
          />
          เฉพาะที่หมดอายุโดยไม่ได้ใช้เกิน {EXPIRED_UNUSED_ALERT_PERCENT}%
        </label>
      </FilterBar>

      <DataTable<Privilege>
        columns={columns.filter((c) => visible.includes(c.id))}
        rows={pageRows}
        rowKey={(p) => p.id}
        caption={`สิทธิพิเศษแบบกดรับ — ${sorted.length} รายการ`}
        state={screenState}
        rowCountHint={10}
        errorMessage="โหลดรายการสิทธิพิเศษไม่สำเร็จ — เชื่อมต่อเซิร์ฟเวอร์ไม่ได้"
        onRetry={() => setScreenState('ready')}
        emptyState={activeFilters.length || query
          ? <EmptyState kind="no-results" title="ไม่พบสิทธิ์ที่ตรงกับตัวกรอง" action={{ label: 'ล้างตัวกรองทั้งหมด', onClick: clearAll }} />
          : <EmptyState kind="no-data" title="ยังไม่มีสิทธิพิเศษ" description="สร้างสิทธิ์แรกเพื่อให้สมาชิกกดรับได้จากหน้าสิทธิประโยชน์ในแอป" />}
        sort={sort}
        onSortChange={setSort}
        onOpenRow={setEdit}
        density={density}
        persistKey="privileges"
        isFlagged={(p) => p.claimed > 0 && pctUnused(p) >= EXPIRED_UNUSED_ALERT_PERCENT}
        className="min-h-0 flex-1 rounded-none border-0"
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        totalItems={sorted.length}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
      />

      {/* ------------------------------------------------------ แก้ไข / สร้าง */}
      <SideDrawer
        open={Boolean(edit)}
        onClose={() => setEdit(null)}
        title={edit?.name ? 'แก้ไขสิทธิพิเศษ' : 'สร้างสิทธิพิเศษใหม่'}
        subtitle={edit?.name || 'สิทธิ์แบบกดรับ — ลูกค้ากดรับแล้วได้รหัสไปใช้'}
        width="lg"
        footer={edit && (
          <div className="flex flex-wrap items-center gap-[var(--space-2)]">
            <button type="button" onClick={() => setConfirmSave(edit)} disabled={invalidReason(edit) !== null} className={primaryBtnCls}>
              บันทึก
            </button>
            <button type="button" onClick={() => setEdit(null)} className={secondaryBtnCls}>ยกเลิก</button>
            {invalidReason(edit) && (
              <span className="inline-flex items-center gap-1 text-[length:var(--text-table-sm)] text-[var(--error-text)]">
                <Icon name="error" size={15} filled />{invalidReason(edit)}
              </span>
            )}
          </div>
        )}
      >
        {edit && (() => {
          const pct = pctUnused(edit);
          const high = edit.claimed > 0 && pct >= EXPIRED_UNUSED_ALERT_PERCENT;
          const pending = Math.max(0, edit.claimed - edit.used - edit.expiredUnused);
          return (
            <div className="flex flex-col gap-[var(--space-4)]">
              {high && (
                <Callout tone="error" title={`หมดอายุโดยไม่ได้ใช้ ${pct}% ของที่กดรับ`}>
                  <p className="m-0 mb-[var(--space-2)]">
                    ลูกค้า {edit.expiredUnused.toLocaleString('th-TH')} ครั้งกดรับแล้วใช้ไม่ทันใน
                    {' '}{formatDuration(edit.codeTtlMinutes)} — สิทธิ์นี้{edit.channel === 'pos' ? 'ต้องไปใช้ที่หน้าร้าน' : edit.channel === 'both' ? 'ใช้ได้ทั้งในแอปและหน้าร้าน' : 'ใช้ในแอป'}
                    {edit.channel !== 'app' ? ' ซึ่งต้องเดินทาง' : ''}
                  </p>
                  <button
                    type="button"
                    onClick={() => setEdit({ ...edit, codeTtlMinutes: edit.codeTtlMinutes < 60 ? 1_440 : 10_080 })}
                    className={secondaryBtnCls}
                  >
                    <Icon name="more_time" size={17} />
                    ขยายเป็น {edit.codeTtlMinutes < 60 ? '1 วัน' : '7 วัน'}
                  </button>
                </Callout>
              )}

              <FormRow label="ชื่อสิทธิ์" required help="ข้อความนี้ลูกค้าเห็นในแอป">
                <input
                  data-autofocus
                  type="text"
                  value={edit.name}
                  onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                  className={`${inputCls} w-full`}
                  placeholder="เช่น ส่วนลด 150 บาท สำหรับสมาชิก Gold"
                />
              </FormRow>

              <FormRow
                label="กลุ่มเป้าหมาย"
                required={requireTier}
                help={requireTier
                  ? 'ต้องเลือกอย่างน้อยหนึ่งระดับจึงจะบันทึกได้'
                  : 'ไม่เลือกเลย = สมาชิกทุกระดับเห็นสิทธิ์นี้'}
              >
                <span className="flex flex-wrap items-center gap-[var(--space-2)]">
                  {tierOptions.map((t) => {
                    const on = edit.audience.tiers.includes(t.id);
                    return (
                      <label
                        key={t.id}
                        className={`flex cursor-pointer items-center gap-[var(--space-15)] rounded-[var(--radius-pill)] border px-[var(--space-3)] py-[var(--space-1)] text-[length:var(--text-form)] ${
                          on ? 'border-[var(--blue-700)] bg-[var(--blue-100)] text-[var(--blue-900)]' : 'border-[var(--line-300)] text-[var(--ink-700)]'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={(e) => setEdit({
                            ...edit,
                            audience: {
                              ...edit.audience,
                              tiers: e.target.checked
                                ? [...edit.audience.tiers, t.id]
                                : edit.audience.tiers.filter((x) => x !== t.id),
                            },
                          })}
                          className="h-4 w-4 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                        />
                        {t.name}
                        <span className="bo-num text-[length:var(--text-table-sm)] text-[var(--ink-700)]">{t.memberCount.toLocaleString('th-TH')} คน</span>
                      </label>
                    );
                  })}
                </span>
              </FormRow>

              {(() => {
                const kind = edit.benefitKind ?? 'percent';
                /* แถมสินค้า/บริการต้องระบุสินค้า ซึ่งหน้านี้ยังเลือกไม่ได้ —
                   เลือกใหม่ไม่ได้ แต่ของเดิมที่ตั้งไว้แล้วยังแก้มูลค่าได้ */
                const productBased = kind === 'product' || kind === 'service';
                return (
                  <section className="flex flex-col gap-[var(--space-3)] rounded-[var(--radius-md)] border border-[var(--line-200)] p-[var(--space-3)]">
                    <h3 className="m-0 text-[length:var(--text-form-lg)] font-[var(--weight-semibold)] text-[var(--ink-900)]">
                      ลูกค้าได้อะไรเมื่อใช้สิทธิ์
                    </h3>

                    <FormRow label="ประเภทสิทธิ์" required>
                      <select
                        value={kind}
                        onChange={(e) => setEdit({ ...edit, benefitKind: e.target.value as PrivilegeBenefitKind })}
                        className={inputCls}
                      >
                        {(Object.keys(PRIVILEGE_BENEFIT_LABEL) as PrivilegeBenefitKind[]).map((k) => (
                          <option key={k} value={k} disabled={(k === 'product' || k === 'service') && !productBased}>
                            {PRIVILEGE_BENEFIT_LABEL[k]}
                            {(k === 'product' || k === 'service') && !productBased ? ' (ยังตั้งจากหน้านี้ไม่ได้)' : ''}
                          </option>
                        ))}
                      </select>
                    </FormRow>

                    <div className="flex flex-wrap items-end gap-[var(--space-4)]">
                      <FormRow
                        label={kind === 'shipping' ? 'วงเงินค่าจัดส่งที่ครอบคลุม' : 'มูลค่าสิทธิ์'}
                        required={kind !== 'shipping'}
                        help={kind === 'shipping' ? 'ใส่ 0 = ครอบคลุมค่าจัดส่งทั้งหมด' : undefined}
                      >
                        <NumberField
                          value={edit.value ?? ''}
                          onChange={(v) => setEdit({ ...edit, value: v === '' ? 0 : v })}
                          unit={valueUnit(kind)}
                          min={0}
                          max={kind === 'percent' ? 100 : undefined}
                          width={168}
                          ariaLabel="มูลค่าสิทธิ์"
                        />
                      </FormRow>

                      {kind === 'percent' && (
                        <FormRow label="ลดสูงสุด" help="เว้นว่าง = ไม่จำกัดเพดาน">
                          <NumberField
                            value={edit.maxDiscount ?? ''}
                            onChange={(v) => setEdit({ ...edit, maxDiscount: v === '' ? null : v })}
                            unit="บาท" min={1} width={168} ariaLabel="เพดานส่วนลด (บาท)"
                          />
                        </FormRow>
                      )}

                      <FormRow label="ยอดซื้อขั้นต่ำ" help="0 = ไม่มีขั้นต่ำ">
                        <NumberField
                          value={edit.minSpend ?? 0}
                          onChange={(v) => setEdit({ ...edit, minSpend: v === '' ? 0 : v })}
                          unit="บาท" min={0} width={168} ariaLabel="ยอดซื้อขั้นต่ำ (บาท)"
                        />
                      </FormRow>
                    </div>

                    {edit.allProducts === false ? (
                      <Callout tone="info" title="สิทธิ์นี้จำกัดเฉพาะสินค้าที่กำหนดไว้">
                        การเลือกรายการสินค้าจะเปิดในเฟสถัดไป — ค่าเดิมจะไม่ถูกเปลี่ยนเมื่อบันทึก
                      </Callout>
                    ) : (
                      <p className="m-0 text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                        <Icon name="info" size={16} className="mr-1 inline align-[-3px]" />
                        ใช้ได้กับสินค้าทุกรายการ — การจำกัดเฉพาะบางสินค้าจะเปิดในเฟสถัดไป
                      </p>
                    )}
                  </section>
                );
              })()}

              <div className="grid grid-cols-2 gap-[var(--space-3)]">
                <FormRow label="เริ่มใช้ได้">
                  <input type="date" value={edit.from} onChange={(e) => setEdit({ ...edit, from: e.target.value })} className={`${inputCls} bo-num`} />
                </FormRow>
                <FormRow label="ใช้ได้ถึง">
                  <input type="date" value={edit.to} onChange={(e) => setEdit({ ...edit, to: e.target.value })} className={`${inputCls} bo-num`} />
                </FormRow>
              </div>

              {/* ------------------------------- อายุของรหัสหลังกดรับ */}
              <section className="flex flex-col gap-[var(--space-2)] rounded-[var(--radius-md)] border-2 border-[var(--blue-200)] p-[var(--space-3)]">
                <h3 className="m-0 flex items-center gap-[var(--space-2)] text-[length:var(--text-form-lg)] font-[var(--weight-semibold)] text-[var(--ink-900)]">
                  <Icon name="timer" size={19} filled className="text-[var(--blue-700)]" />
                  อายุของรหัสหลังลูกค้ากดรับ
                </h3>
                <p className="m-0 text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                  นับจากวินาทีที่กดรับ ไม่ใช่จากวันที่แคมเปญเริ่ม — เมื่อหมดเวลารหัสหายและสิทธิ์คืนเข้าโควตา
                </p>

                <div className="flex flex-wrap gap-[var(--space-15)]">
                  {TTL_PRESETS.map((t) => {
                    const on = edit.codeTtlMinutes === t.minutes;
                    return (
                      <button
                        key={t.minutes}
                        type="button"
                        onClick={() => setEdit({ ...edit, codeTtlMinutes: t.minutes })}
                        className={`min-h-[var(--hit-control)] rounded-[var(--radius-pill)] border px-[var(--space-3)] text-[length:var(--text-form)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2 ${
                          on ? 'border-[var(--blue-700)] bg-[var(--blue-700)] font-[var(--weight-semibold)] text-white' : 'border-[var(--line-300)] text-[var(--ink-700)] hover:bg-[var(--surface-100)]'
                        }`}
                      >
                        {t.label}
                        {t.minutes === DEFAULT_CODE_TTL_MINUTES && !on && (
                          <span className="ml-1 text-[length:var(--text-table-sm)] text-[var(--ink-600)]">ค่าตั้งต้น</span>
                        )}
                      </button>
                    );
                  })}
                </div>

                <p className="m-0 text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)]">
                  {TTL_PRESETS.find((t) => t.minutes === edit.codeTtlMinutes)?.note
                    ?? `ค่าที่ตั้งเอง ${formatDuration(edit.codeTtlMinutes)}`}
                </p>

                <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
                  หรือตั้งเอง
                  <NumberField
                    value={edit.codeTtlMinutes}
                    onChange={(v) => setEdit({ ...edit, codeTtlMinutes: v === '' ? DEFAULT_CODE_TTL_MINUTES : v })}
                    unit="นาที" min={1} width={168} ariaLabel="อายุของรหัสหลังกดรับ (นาที)"
                  />
                  <span className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">= {formatDuration(edit.codeTtlMinutes)}</span>
                </label>

                {edit.codeTtlMinutes > 1_440 && (
                  <Callout tone="warning" title="เวลานี้ยาวพอให้คนกดเก็บรหัสไว้ล่วงหน้า">
                    ถ้าสิทธิ์นี้มีจำนวนจำกัด รหัสที่ถูกกดเก็บไว้จะกินโควตาตั้งแต่วินาทีที่กดรับ
                    ทำให้คนที่ตั้งใจใช้ทันทีกดรับไม่ได้ — สิทธิ์ที่จำกัดจำนวนไม่ควรตั้งเกิน 1 วัน
                    เว้นแต่ยอมให้โควตาถูกจองไว้
                  </Callout>
                )}
                {edit.codeTtlMinutes <= 15 && edit.channel !== 'app' && (
                  <Callout tone="warning" title="สิทธิ์ที่ใช้หน้าร้านกับเวลา 15 นาทีมักไม่พอ">
                    ลูกค้าที่กดรับตอนเห็นแบนเนอร์แล้วตั้งใจไปใช้ที่ร้านจะเสียสิทธิ์ก่อนถึงร้าน
                    ดูตัวเลข &quot;หมดอายุโดยไม่ได้ใช้&quot; ของสิทธิ์เดิมที่ตั้งค่าคล้ายกันก่อนตัดสินใจ
                  </Callout>
                )}
              </section>

              <div className="grid grid-cols-2 gap-[var(--space-3)]">
                <FormRow
                  label="กดรับได้กี่ครั้งต่อคน"
                  help="นับใหม่ทุกรอบตามที่เลือกด้านขวา ไม่ใช่ตลอดแคมเปญ"
                >
                  <span className="flex flex-wrap items-center gap-[var(--space-2)]">
                    <NumberField
                      value={edit.limitPerPerson ?? ''}
                      onChange={(v) => setEdit({ ...edit, limitPerPerson: v === '' ? null : v })}
                      unit="ครั้ง" min={1} width={148}
                      disabled={edit.limitPerPerson === null}
                      ariaLabel="สิทธิ์ต่อคน"
                    />
                    <button
                      type="button"
                      onClick={() => setEdit({ ...edit, limitPerPerson: edit.limitPerPerson === null ? 1 : null })}
                      className={secondaryBtnCls}
                    >
                      {edit.limitPerPerson === null ? 'ตั้งเพดาน' : 'ไม่จำกัด'}
                    </button>
                    <select
                      value={edit.recurrence ?? 'monthly'}
                      onChange={(e) => setEdit({ ...edit, recurrence: e.target.value as PrivilegeRecurrence })}
                      className={inputCls}
                      aria-label="รอบที่นับเพดานต่อคนใหม่"
                    >
                      {(Object.keys(PRIVILEGE_RECURRENCE_LABEL) as PrivilegeRecurrence[]).map((r) => (
                        <option key={r} value={r}>{PRIVILEGE_RECURRENCE_LABEL[r]}</option>
                      ))}
                    </select>
                  </span>
                </FormRow>

                <FormRow label="จำนวนสิทธิ์ทั้งหมด" help="เพดานรวมทุกคนตลอดแคมเปญ">
                  <span className="flex items-center gap-[var(--space-2)]">
                    <NumberField
                      value={edit.totalLimit ?? ''}
                      onChange={(v) => setEdit({ ...edit, totalLimit: v === '' ? null : v })}
                      unit="สิทธิ์" min={1} width={148}
                      disabled={edit.totalLimit === null || edit.totalLimit === undefined}
                      ariaLabel="จำนวนสิทธิ์ทั้งหมด"
                    />
                    <button
                      type="button"
                      onClick={() => setEdit({ ...edit, totalLimit: edit.totalLimit === null || edit.totalLimit === undefined ? 1_000 : null })}
                      className={secondaryBtnCls}
                    >
                      {edit.totalLimit === null || edit.totalLimit === undefined ? 'ตั้งเพดาน' : 'ไม่จำกัด'}
                    </button>
                  </span>
                </FormRow>
                <FormRow label="ใช้ได้ที่ไหน">
                  <select
                    value={edit.channel}
                    onChange={(e) => setEdit({ ...edit, channel: e.target.value as Privilege['channel'] })}
                    className={inputCls}
                  >
                    {(Object.keys(CHANNEL_LABEL) as Array<Privilege['channel']>).map((c) => (
                      <option key={c} value={c}>{CHANNEL_LABEL[c]}</option>
                    ))}
                  </select>
                </FormRow>
              </div>

              <ImageDropzone
                /* ⚠️ ต้องส่ง file กลับเข้าไปด้วย ไม่งั้นไฟล์หายตอนพิมพ์คำอธิบายภาพ */
                value={edit.imageUrl ? { url: edit.imageUrl, alt: edit.imageAlt ?? '', file: edit.imageFile } : null}
                onChange={(v: ImageValue | null) => setEdit({
                  ...edit, imageUrl: v?.url ?? null, imageAlt: v?.alt ?? '', imageFile: v?.file,
                })}
                spec={IMAGE_SPEC}
                label="ภาพสิทธิพิเศษ"
                previewWidth={280}
              />

              <FormRow label="สถานะ" help="ฉบับร่างยังไม่แสดงในแอป · พักไว้ทำให้คนที่ถือรหัสอยู่แล้วยังใช้ได้ แต่กดรับใหม่ไม่ได้">
                <select
                  value={edit.status}
                  onChange={(e) => setEdit({ ...edit, status: e.target.value as PrivilegeStatus })}
                  className={inputCls}
                  style={{ minWidth: 220 }}
                >
                  {(Object.keys(PRIVILEGE_STATUS_LABEL) as PrivilegeStatus[]).map((s) => (
                    <option key={s} value={s}>{PRIVILEGE_STATUS_LABEL[s]}</option>
                  ))}
                </select>
              </FormRow>

              {/* ------------------------------------------------ สถิติ */}
              <section className="flex flex-col gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-100)] p-[var(--space-3)]">
                <h3 className="m-0 text-[length:var(--text-form-lg)] font-[var(--weight-semibold)] text-[var(--ink-900)]">สถิติของสิทธิ์นี้</h3>
                {edit.claimed === 0 ? (
                  <p className="m-0 text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-600)]">
                    ยังไม่มีใครกดรับ — สถิติจะเริ่มนับเมื่อสิทธิ์เปิดใช้งาน
                  </p>
                ) : (
                  <>
                    {!hideUsed && <ClaimFunnel p={edit} height={14} />}
                    <div className="grid grid-cols-2 gap-[var(--space-3)]">
                      <Field label="กดรับไปแล้ว">
                        <span className="bo-num" style={{ textAlign: 'left' }}>{edit.claimed.toLocaleString('th-TH')} ครั้ง</span>
                      </Field>
                      {!hideUsed && (
                        <>
                          <Field label="ใช้จริง" hint={`${pctUsed(edit)}% ของที่กดรับ`}>
                            <span className="bo-num" style={{ textAlign: 'left' }}>{edit.used.toLocaleString('th-TH')} ครั้ง</span>
                          </Field>
                          <Field label="ยังไม่ถึงกำหนดใช้" hint="ถือรหัสอยู่และยังไม่หมดเวลา">
                            <span className="bo-num" style={{ textAlign: 'left' }}>{pending.toLocaleString('th-TH')} ครั้ง</span>
                          </Field>
                        </>
                      )}
                      <Field
                        label="หมดอายุโดยไม่ได้ใช้"
                        tone={high ? 'error' : 'default'}
                        hint={high ? `${pct}% — สูงกว่าเกณฑ์ ${EXPIRED_UNUSED_ALERT_PERCENT}% ที่ถือว่าผิดปกติ` : `${pct}% ของที่กดรับ`}
                      >
                        <span className="bo-num" style={{ textAlign: 'left' }}>{edit.expiredUnused.toLocaleString('th-TH')} ครั้ง</span>
                      </Field>
                    </div>
                    <p className="m-0 border-t border-[var(--line-200)] pt-[var(--space-2)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                      <Icon name="lightbulb" size={16} className="mr-1 inline align-[-3px]" />
                      ตัวเลขที่ควรอ่านคู่กันคือ &quot;ใช้จริง&quot; กับ &quot;หมดอายุโดยไม่ได้ใช้&quot; —
                      ถ้าทั้งสองสูงพร้อมกัน แคมเปญน่าสนใจแต่เวลาสั้นเกินไป
                      ถ้ากดรับน้อยตั้งแต่ต้น ปัญหาอยู่ที่การมองเห็นสิทธิ์ในแอป ไม่ใช่ที่เวลา
                    </p>
                  </>
                )}
              </section>

              <p className="m-0 text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-600)]">
                <Icon name="info" size={16} className="mr-1 inline align-[-3px]" />
                รหัสที่ลูกค้าถืออยู่แล้วไม่ถูกกระทบเมื่อแก้อายุรหัส — ค่าใหม่ใช้กับการกดรับครั้งถัดไปเท่านั้น
              </p>
            </div>
          );
        })()}
      </SideDrawer>

      <ConfirmDialog
        open={Boolean(confirmSave)}
        tone={confirmSave && confirmSave.status === 'active' ? 'danger' : 'default'}
        title="บันทึกสิทธิพิเศษ"
        description={confirmSave ? (
          <>
            <p className="m-0 mb-[var(--space-2)]">
              {confirmSave.name} · อายุรหัสหลังกดรับ{' '}
              <strong className="bo-num">{formatDuration(confirmSave.codeTtlMinutes)}</strong> ·{' '}
              {confirmSave.audience.tiers.length === 0
                ? 'สมาชิกทุกระดับ'
                : confirmSave.audience.tiers.map((t) => TIERS.find((x) => x.id === t)?.name).join(' · ')}
            </p>
            {confirmSave.status === 'active' && (
              <p className="m-0 font-[var(--weight-medium)] text-[var(--ink-900)]">
                สถานะ &quot;ใช้งานอยู่&quot; — สิทธิ์นี้จะขึ้นบนแอปทันทีที่บันทึก
              </p>
            )}
          </>
        ) : ''}
        confirmLabel="บันทึก"
        requireReason
        reasonLabel="เหตุผลที่แก้"
        reasonPresets={[
          'ขยายอายุรหัสเพราะอัตราหมดอายุโดยไม่ได้ใช้สูง',
          'ปรับกลุ่มเป้าหมายตามแผนการตลาด',
          'แก้ข้อความให้ตรงกับที่ประกาศไว้',
        ]}
        onConfirm={(reason) => confirmSave && save(confirmSave, reason)}
        onCancel={() => setConfirmSave(null)}
      />
    </div>
  );
};

export default PrivilegeScreen;

/* ============================================================================
   รองรับ:
   SRS 3.4     สิทธิ์แต่ละรายการมีปุ่มกดรับ และบันทึกว่าใช้ไปแล้วกี่ครั้ง
   FR-ADM-19   สร้าง/แก้ไข/หยุดแคมเปญได้เองโดยไม่ต้องเขียนโปรแกรม
   FR-ADM-23   สิทธิประโยชน์ผูกกับระดับสมาชิกที่ตั้งในหน้า TierScreen
   FR-ADM-37   ทุกการแก้ไขเข้า Audit Log พร้อมเหตุผล
   FR-ADM-39   การส่งออกถูกบันทึกและแจ้งผู้ใช้
   FR-NT-02    การกดรับและการหมดอายุของรหัสต้องมีการแจ้งเตือนถึงลูกค้า

   ประเด็นค้าง (⛔):
   — อายุรหัส 15 นาทีมาจาก mockup แอป ไม่ได้อยู่ใน SRS ต้องยืนยันก่อนขึ้นระบบ
   — เมื่อรหัสหมดอายุ สิทธิ์ควรคืนเข้าโควตาให้คนอื่นกดรับได้ หรือถือว่าใช้ไปแล้ว
     (หน้านี้สมมติว่าคืนเข้าโควตา — ถ้าไม่คืน ตัวเลขโควตาคงเหลือจะผิด)
   — การแก้อายุรหัสกระทบรหัสที่ลูกค้าถืออยู่แล้วหรือไม่ (หน้านี้สมมติว่าไม่กระทบ)
   — ยังไม่มีการจำกัดจำนวนสิทธิ์รวมทั้งแคมเปญ มีแต่จำกัดต่อคน
     ถ้าต้องมี ต้องเพิ่มช่องโควตารวมและตัวนับที่แสดงในตาราง
   — เกณฑ์ 25% ที่ถือว่า "สูงผิดปกติ" เป็นค่าที่ทีมตั้ง ควรทบทวนหลังเก็บข้อมูลจริง 1 ไตรมาส
   ============================================================================ */
